import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Building2, Hammer, Layers, Truck, ClipboardList, Wrench, Zap, Wind,
  PaintBucket, Cog, Plus, Minus, Eye, GripVertical, Info, Lock, Settings2, Check,
  Calendar, Loader2,
} from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =================================================================
// LOCAL DATE HELPERS (use shared formatDate for MM/DD/YYYY)
// =================================================================
const formatMDY = formatDate;
function addDays(base, days) {
  const dt = new Date(base);
  dt.setDate(dt.getDate() + days);
  return dt;
}
function toInputDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  // Use LOCAL date to avoid tz-shift wiping a day on iso
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Multi-Unit Schedule Preview — visual mockup for the Phase 2 wizard's Step 4.
 *
 * Demonstrates:
 *  - Per-phase mode toggle: whole-project | per-building | per-unit
 *  - Contractor bid input: "days per unit" (or "days for project")
 *  - Auto-stagger logic with configurable 1-day lag
 *  - Two visualizations side-by-side: Gantt swimlanes vs flat list
 *
 * Demo project: Hampden Townhomes — 4 units (per-unit scope after framing).
 */

// =================================================================
// PHASE TEMPLATE — matches 8-phase server template
// =================================================================
const PHASES = [
  { id: 'permits',   n: 1, name: 'Planning & Permits',     icon: ClipboardList, default_mode: 'whole_project',  default_days: 30, default_per_unit: null, color: '#64748b' },
  { id: 'site',      n: 2, name: 'Site Prep & Foundation', icon: Truck,         default_mode: 'whole_project',  default_days: 18, default_per_unit: null, color: '#a16207' },
  { id: 'framing',   n: 3, name: 'Framing',                icon: Hammer,        default_mode: 'per_building',   default_days: null, default_per_unit: 4,  color: '#ea580c' },
  { id: 'roughin',   n: 4, name: 'Exterior & Rough-in',    icon: Layers,        default_mode: 'per_unit',       default_days: null, default_per_unit: 6,  color: '#0ea5e9' },
  { id: 'drywall',   n: 5, name: 'Insulation & Drywall',   icon: Layers,        default_mode: 'per_unit',       default_days: null, default_per_unit: 3,  color: '#7c3aed' },
  { id: 'interior',  n: 6, name: 'Interior Finishes',      icon: PaintBucket,   default_mode: 'per_unit',       default_days: null, default_per_unit: 5,  color: '#db2777' },
  { id: 'mech',      n: 7, name: 'Mech. Trim & Fixtures',  icon: Cog,           default_mode: 'per_unit',       default_days: null, default_per_unit: 2,  color: '#0891b2' },
  { id: 'punch',     n: 8, name: 'Punch List & Close-out', icon: Check,         default_mode: 'whole_project',  default_days: 7,  default_per_unit: null, color: '#16a34a' },
];

const MODE_LABEL = {
  whole_project: 'Whole project',
  per_building:  'Per building',
  per_unit:      'Per unit',
};

const MODE_TONE = {
  whole_project: 'bg-slate-100 text-slate-700 border-slate-300',
  per_building:  'bg-amber-50 text-amber-800 border-amber-200',
  per_unit:      'bg-blue-50 text-blue-800 border-blue-200',
};

// =================================================================
// SCHEDULER — REMOVED: now computed by backend POST /api/projects/schedule/preview
// =================================================================

// =================================================================
// MAIN
// =================================================================
export default function MultiUnitSchedulePreview() {
  const [unitCount, setUnitCount] = useState(4);
  const [lagDays, setLagDays] = useState(1);
  const [view, setView] = useState('flat'); // 'gantt' | 'flat' | 'both'
  const [startDate, setStartDate] = useState(() => {
    // default = next Monday
    const d = new Date();
    d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
    return d;
  });
  const [phases, setPhases] = useState(() =>
    PHASES.map(p => ({
      id: p.id, name: p.name, icon: p.icon, color: p.color,
      mode: p.default_mode,
      days: p.default_days,
      per_unit: p.default_per_unit,
      // varied default per-building array (so user sees the feature)
      per_building_days: p.id === 'framing' ? [4, 6, 4, 5] : Array(4).fill(p.default_per_unit || 3),
    }))
  );
  const [bars, setBars] = useState([]);
  const [totalDays, setTotalDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Resize per_building_days when unit count changes
  useEffect(() => {
    setPhases(prev => prev.map(p => {
      const cur = p.per_building_days || [];
      if (cur.length === unitCount) return p;
      const next = [...cur];
      while (next.length < unitCount) next.push(cur[cur.length - 1] || 4);
      next.length = unitCount;
      return { ...p, per_building_days: next };
    }));
  }, [unitCount]);

  // Call backend on any change (debounced)
  useEffect(() => {
    const handle = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const body = {
          start_date: toInputDate(startDate),
          unit_count: unitCount,
          lag_days: lagDays,
          phases: phases.map(p => ({
            id: p.id,
            name: p.name,
            mode: p.mode,
            days: p.mode === 'whole_project' ? (p.days || 0) : null,
            per_unit: p.mode === 'per_unit' ? (p.per_unit || 0) : null,
            per_building_days: p.mode === 'per_building' ? (p.per_building_days || []) : null,
          })),
        };
        const res = await axios.post(`${API}/projects/schedule/preview`, body, { signal: controller.signal });
        // Normalize to existing view shape: { phase_id, unit, start, end }
        setBars((res.data.bars || []).map(b => ({
          phase_id: b.phase_id,
          phase_name: b.phase_name,
          unit: b.unit,
          start: b.start_day,
          end: b.end_day,
          start_date: b.start_date, // already MM/DD/YYYY from backend
          end_date: b.end_date,
        })));
        setTotalDays(res.data.total_days || 0);
      } catch (e) {
        if (!axios.isCancel(e) && e.name !== 'CanceledError') {
          setError(e?.response?.data?.detail || e.message || 'Schedule preview failed');
        }
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [phases, unitCount, lagDays, startDate]);

  const updatePhase = (id, patch) => {
    setPhases(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };
  const updateBuildingDay = (id, idx, value) => {
    setPhases(prev => prev.map(p => {
      if (p.id !== id) return p;
      const next = [...(p.per_building_days || [])];
      next[idx] = parseInt(value) || 0;
      return { ...p, per_building_days: next };
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="multi-unit-preview-page">
      {/* ============ HEADER ============ */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1440px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/project-hub-phase2-preview" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Phase 2 mockup
            </Link>
            <span className="text-slate-300">/</span>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-700" />
              <span className="font-semibold text-slate-900">Multi-Unit Schedule — Wizard Step 4 preview</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">DEMO</span>
            <span className="text-slate-500">Hampden Townhomes</span>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6 space-y-6">

        {/* ============ EXPLAINER ============ */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 flex gap-3">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">How the staggering works</div>
            <ul className="list-disc ml-5 space-y-0.5">
              <li><b>Whole project</b> — one bar across all units (Permits, Foundation, Punch list).</li>
              <li><b>Per building</b> — contractor enters days for each building; bars run back-to-back as crew finishes a building (Framing).</li>
              <li><b>Per unit</b> — contractor enters days per unit; the next trade starts on Unit 1 the day after the previous trade finishes Unit 1 (+ 1-day lag), while the previous trade rolls into Unit 2.</li>
              <li>You can override any contractor's bid days or lag manually before sending the schedule cascade.</li>
            </ul>
          </div>
        </div>

        {/* ============ TOP CONTROLS ============ */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Start</span>
            <input
              type="date"
              value={toInputDate(startDate)}
              onChange={e => setStartDate(new Date(e.target.value + 'T00:00:00'))}
              className="px-2 py-1.5 text-sm font-semibold border border-slate-200 rounded-lg"
              data-testid="start-date-input"
            />
            <span className="text-xs text-slate-500 tabular-nums" data-testid="start-date-display">
              {formatMDY(startDate)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Units</span>
            <div className="inline-flex items-center bg-slate-100 rounded-lg">
              <button
                onClick={() => setUnitCount(Math.max(1, unitCount - 1))}
                className="p-2 hover:bg-slate-200 rounded-l-lg" data-testid="unit-decrement">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-4 font-bold text-slate-900" data-testid="unit-count">{unitCount}</span>
              <button
                onClick={() => setUnitCount(Math.min(8, unitCount + 1))}
                className="p-2 hover:bg-slate-200 rounded-r-lg" data-testid="unit-increment">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Default lag</span>
            <div className="inline-flex items-center bg-slate-100 rounded-lg">
              <button onClick={() => setLagDays(Math.max(0, lagDays - 1))} className="p-2 hover:bg-slate-200 rounded-l-lg">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-3 font-bold text-slate-900">{lagDays}d</span>
              <button onClick={() => setLagDays(Math.min(7, lagDays + 1))} className="p-2 hover:bg-slate-200 rounded-r-lg">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">View</span>
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              {['gantt', 'flat', 'both'].map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                    view === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`view-${v}`}>
                  {v === 'gantt' ? 'Gantt swimlanes' : v === 'flat' ? 'Flat list' : 'Side-by-side'}
                </button>
              ))}
            </div>
          </div>

          <div className="text-right border-l border-slate-200 pl-6">
            <div className="text-xs text-slate-500 uppercase tracking-wide flex items-center justify-end gap-1.5">
              Total duration
              {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
            </div>
            <div className="text-2xl font-bold text-slate-900" data-testid="total-days">{totalDays} days</div>
            <div className="text-xs text-slate-600 tabular-nums mt-0.5" data-testid="end-date-display">
              {formatMDY(startDate)} → {formatMDY(addDays(startDate, totalDays))}
            </div>
            {error && <div className="text-[11px] text-rose-600 mt-1 max-w-[180px]">{error}</div>}
          </div>
        </div>

        {/* ============ PHASE EDITOR ============ */}
        <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Phase bid configuration
            </h2>
            <span className="text-xs text-slate-500">Click a phase to switch its mode or edit its days</span>
          </div>
          <div className="divide-y divide-slate-100">
            {phases.map(p => {
              const Icon = p.icon;
              const buildingTotal = (p.per_building_days || []).slice(0, unitCount).reduce((s, n) => s + (n || 0), 0);
              return (
                <div key={p.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`phase-row-${p.id}`}>
                  <div className="flex items-center gap-4">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${p.color}20`, color: p.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">
                        {p.mode === 'whole_project' && `Crew works the entire project at once · ${p.days || 0} days`}
                        {p.mode === 'per_building' && `Crew finishes a building, then starts the next · ${buildingTotal} days total across ${unitCount} buildings`}
                        {p.mode === 'per_unit' && `Crew rolls unit-to-unit, next trade follows · ${p.per_unit || 0} days/unit`}
                      </div>
                    </div>

                    {/* mode toggle */}
                    <div className="inline-flex bg-slate-100 rounded-lg p-1">
                      {['whole_project', 'per_building', 'per_unit'].map(m => (
                        <button
                          key={m}
                          onClick={() => updatePhase(p.id, { mode: m })}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            p.mode === m ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                          }`}
                          data-testid={`mode-${p.id}-${m}`}>
                          {MODE_LABEL[m]}
                        </button>
                      ))}
                    </div>

                    {/* days input (single number for whole_project / per_unit only) */}
                    <div className="flex items-center gap-2 w-28 justify-end">
                      {p.mode === 'whole_project' && (
                        <>
                          <input
                            type="number" min="0" value={p.days ?? ''}
                            onChange={e => updatePhase(p.id, { days: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 text-sm font-semibold text-right border border-slate-200 rounded"
                            data-testid={`days-${p.id}`}
                          />
                          <span className="text-xs text-slate-500 w-8">days</span>
                        </>
                      )}
                      {p.mode === 'per_unit' && (
                        <>
                          <input
                            type="number" min="0" value={p.per_unit ?? ''}
                            onChange={e => updatePhase(p.id, { per_unit: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 text-sm font-semibold text-right border border-slate-200 rounded"
                            data-testid={`per-unit-${p.id}`}
                          />
                          <span className="text-xs text-slate-500 w-8">d/unit</span>
                        </>
                      )}
                      {p.mode === 'per_building' && (
                        <span className="text-xs text-slate-500 italic">see below</span>
                      )}
                    </div>
                  </div>

                  {/* per-building inputs row */}
                  {p.mode === 'per_building' && (
                    <div className="mt-3 ml-14 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid={`per-building-grid-${p.id}`}>
                      {Array.from({ length: unitCount }).map((_, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 bg-amber-50/60 border border-amber-200 rounded-md px-2 py-1.5">
                          <span className="text-[11px] font-semibold text-amber-900 uppercase whitespace-nowrap">Bldg {idx + 1}</span>
                          <input
                            type="number" min="0"
                            value={(p.per_building_days || [])[idx] ?? ''}
                            onChange={e => updateBuildingDay(p.id, idx, e.target.value)}
                            className="w-14 px-1.5 py-0.5 text-sm font-semibold text-right border border-amber-300 rounded bg-white"
                            data-testid={`per-building-${p.id}-${idx}`}
                          />
                          <span className="text-[11px] text-amber-700">d</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ============ VISUALIZATIONS ============ */}
        <div className={`grid gap-6 ${view === 'both' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
          {(view === 'gantt' || view === 'both') && (
            <GanttView phases={phases} bars={bars} totalDays={totalDays} unitCount={unitCount} startDate={startDate} />
          )}
          {(view === 'flat' || view === 'both') && (
            <FlatListView phases={phases} bars={bars} totalDays={totalDays} startDate={startDate} />
          )}
        </div>

        {/* ============ Q5 CLARIFY BOX ============ */}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3">
          <Lock className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Q5 clarified — one contractor per trade across all units</div>
            <p>
              Yes, that's what I was asking: e.g., whether you'd ever award two different plumbers, one to Units 1–2 and another to Units 3–4. <b>I'll default to one contractor per trade across all units (your model)</b>, which keeps the bid sheet and schedule cascade simple. If you ever need to split a trade later, we can add it as an "edge case" enhancement without rebuilding the core.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pb-12">
          <Link to="/project-hub-phase2-preview" className="text-sm text-slate-600 hover:text-slate-900 underline">
            Back to Phase 2 overview
          </Link>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm">
            <Check className="w-4 h-4" /> Approve this approach &amp; start build
          </button>
        </div>
      </main>
    </div>
  );
}

// =================================================================
// GANTT SWIMLANE VIEW
// =================================================================
function GanttView({ phases, bars, totalDays, unitCount, startDate }) {
  // Width per day in px
  const dayWidth = Math.max(6, Math.min(14, 900 / totalDays));
  const rowH = 28;
  const phaseById = Object.fromEntries(phases.map(p => [p.id, p]));

  // Build swimlanes: one row per (phase) when whole_project, or one row per unit otherwise
  // To make this readable, we render per-unit lanes (1 row per unit) and stack bars there.
  const unitLanes = Array.from({ length: unitCount }, (_, i) => i + 1);

  return (
    <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="gantt-view">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Eye className="w-4 h-4" /> Gantt — swimlanes per unit
        </h2>
        <span className="text-xs text-slate-500">{totalDays} days total · zoom auto-fit</span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-full" style={{ width: dayWidth * totalDays + 160 }}>
          {/* timeline axis */}
          <div className="flex border-b border-slate-200 bg-slate-50/60 sticky top-0">
            <div className="w-40 shrink-0 px-3 py-2 text-xs font-semibold text-slate-600 border-r border-slate-200">
              Unit
            </div>
            <div className="relative flex-1 h-9">
              {Array.from({ length: Math.ceil(totalDays / 10) + 1 }).map((_, i) => {
                const d = addDays(startDate, i * 10);
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 border-l border-slate-200 text-[10px] text-slate-500 pl-1"
                    style={{ left: i * 10 * dayWidth }}>
                    {mm}/{dd}
                  </div>
                );
              })}
            </div>
          </div>

          {/* whole-project lane */}
          <UnitLane
            label="All units"
            tone="bg-slate-50"
            barsForUnit={bars.filter(b => b.unit === 'all')}
            phaseById={phaseById}
            dayWidth={dayWidth}
            totalDays={totalDays}
            rowH={rowH}
            startDate={startDate}
          />

          {/* per-unit lanes */}
          {unitLanes.map(u => (
            <UnitLane
              key={u}
              label={`Unit ${u}`}
              barsForUnit={bars.filter(b => b.unit === u)}
              phaseById={phaseById}
              dayWidth={dayWidth}
              totalDays={totalDays}
              rowH={rowH}
              startDate={startDate}
            />
          ))}
        </div>
      </div>

      {/* legend */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-3">
        {phases.map(p => (
          <div key={p.id} className="flex items-center gap-1.5 text-[11px]">
            <span className="w-3 h-3 rounded" style={{ background: p.color }} />
            <span className="text-slate-700">{p.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function UnitLane({ label, tone = '', barsForUnit, phaseById, dayWidth, totalDays, rowH, startDate }) {
  return (
    <div className={`flex border-b border-slate-100 ${tone}`}>
      <div className="w-40 shrink-0 px-3 py-2 text-xs font-semibold text-slate-700 border-r border-slate-200 flex items-center">
        {label}
      </div>
      <div className="relative flex-1" style={{ height: rowH + 8 }}>
        {barsForUnit.map((b, i) => {
          const p = phaseById[b.phase_id];
          const left = b.start * dayWidth;
          const width = Math.max(2, (b.end - b.start) * dayWidth);
          const startStr = formatMDY(addDays(startDate, b.start));
          const endStr = formatMDY(addDays(startDate, b.end));
          return (
            <div
              key={`${b.phase_id}-${b.unit}-${i}`}
              className="absolute top-1 rounded text-[10px] text-white font-semibold px-1.5 flex items-center overflow-hidden shadow-sm"
              style={{ left, width, height: rowH, background: p.color }}
              title={`${p.name} · ${startStr} → ${endStr} (${b.end - b.start}d)`}>
              <span className="truncate">{p.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =================================================================
// FLAT LIST VIEW
// =================================================================
function FlatListView({ phases, bars, totalDays, startDate }) {
  const phaseById = Object.fromEntries(phases.map(p => [p.id, p]));
  // sort bars by start day, whole-project bars first on tie
  const sorted = [...bars].sort((a, b) => a.start - b.start || (a.unit === 'all' ? -1 : 1));
  const projectEnd = addDays(startDate, totalDays);

  return (
    <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="flat-view">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Eye className="w-4 h-4" /> Schedule — flat list
        </h2>
        <div className="text-xs text-slate-600 tabular-nums">
          <span className="font-semibold">{formatMDY(startDate)}</span>
          <span className="mx-1.5 text-slate-400">→</span>
          <span className="font-semibold">{formatMDY(projectEnd)}</span>
          <span className="ml-2 text-slate-500">({sorted.length} entries · {totalDays}d)</span>
        </div>
      </div>

      {/* table header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/70 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <div className="col-span-5">Phase</div>
        <div className="col-span-2">Scope</div>
        <div className="col-span-2 text-right tabular-nums">Start</div>
        <div className="col-span-2 text-right tabular-nums">End</div>
        <div className="col-span-1 text-right">Days</div>
      </div>

      <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
        {sorted.map((b, i) => {
          const p = phaseById[b.phase_id];
          const startStr = formatMDY(addDays(startDate, b.start));
          const endStr = formatMDY(addDays(startDate, b.end));
          return (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-slate-50">
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div className="w-2 h-8 rounded shrink-0" style={{ background: p.color }} />
                <div className="font-medium text-sm text-slate-900 truncate">{p.name}</div>
              </div>
              <div className="col-span-2">
                {b.unit === 'all' ? (
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold">
                    All units
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold">
                    Unit {b.unit}
                  </span>
                )}
              </div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700" data-testid={`row-${i}-start`}>
                {startStr}
              </div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700" data-testid={`row-${i}-end`}>
                {endStr}
              </div>
              <div className="col-span-1 text-right text-sm font-bold text-slate-900">
                {b.end - b.start}d
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
